import React, { createContext, useContext, useEffect, useState } from "react";

import {
  InterviewJobInfo,
  findInterviewJobInfo,
  getIsInterviewOpen,
} from "apis/recruit";

interface InterviewContextType {
  interviewDetails: InterviewJobInfo | undefined | null;
  isVideoEnabled: boolean;
  isInterviewScreenOpen: boolean | undefined;
  interviewStatusCheckLoaded: boolean;
  showErrorScreen: boolean;
  interviewFlowId: string;
  redirectUrl?: string;
}

const InterviewContext = createContext<InterviewContextType | undefined>(
  undefined
);

export const useInterview = () => {
  const context = useContext(InterviewContext);
  if (!context) {
    throw new Error("useInterview must be used within an InterviewProvider");
  }
  return context;
};

export const InterviewProvider: React.FC<{
  interviewFlowId: string;
  children: React.ReactNode;
}> = ({ interviewFlowId, children }) => {
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [interviewDetails, setInterviewDetails] = useState<InterviewJobInfo>();
  const [isInterviewScreenOpen, setIsInterviewScreenOpen] = useState<
    boolean | undefined
  >();

  const [interviewStatusCheckLoaded, setInterviewStatusCheckLoaded] =
    useState(false);

  useEffect(() => {
    const get = async () => {
      const details = await findInterviewJobInfo(interviewFlowId);
      if (details) setInterviewDetails(details);
      else setShowErrorScreen(true); // if no details were found, interviewFlowId is likely wrong
    };
    get();
  }, [interviewFlowId]);

  useEffect(() => {
    const get = async () => {
      const { isInterviewOpen } = await getIsInterviewOpen({
        interviewFlowId: interviewFlowId,
      });
      setInterviewStatusCheckLoaded(true);
      setIsInterviewScreenOpen(isInterviewOpen || false);
    };
    get();
  }, [interviewFlowId]);

  const value = {
    interviewDetails,
    isVideoEnabled: interviewDetails?.isVideoEnabled || false,
    isInterviewScreenOpen,
    interviewStatusCheckLoaded,
    showErrorScreen,
    interviewFlowId: interviewFlowId,
    redirectUrl: interviewDetails?.redirectUrl,
  };

  return (
    <InterviewContext.Provider value={value}>
      {children}
    </InterviewContext.Provider>
  );
};
